import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, API_URL, getAdminToken } from '../utils/api';
import ReportFilters from './ReportFilters';

const TreatmentSummary = () => {
  const { print_lang, currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const printRef = useRef();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branch_id) params.append('branch_id', filters.branch_id);
      if (filters.preset) params.append('preset', filters.preset);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);

      const response = await apiGet(`/api/admin/reports/treatments?${params.toString()}`);
      if (response.success) {
        setData(response.data);
      }
    } catch (err) {
      console.error(err.message || 'Failed to load treatment summary', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (filters.branch_id) params.append('branch_id', filters.branch_id);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);

    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/reports/export/treatments?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `treatment_report_${filters.from_date || 'all'}_${filters.to_date || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export report');
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${print_lang('reports.treatments', 'Treatment Summary')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            h1 { margin-bottom: 20px; }
            h3 { margin-top: 30px; margin-bottom: 10px; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div>
      <ReportFilters
        onFilterChange={setFilters}
        showBranchFilter={true}
        showDateFilter={true}
        onExport={handleExport}
        onPrint={handlePrint}
        loading={loading}
      />

      {loading ? (
        <div className="card">
          <div className="loading-container">
            <div className="spinner" />
          </div>
        </div>
      ) : data ? (
        <div ref={printRef}>
          <h2 style={{ marginBottom: '1rem' }}>{print_lang('reports.treatments', 'Treatment Summary')}</h2>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="card" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.totalTreatments', 'Total Treatments')}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{data.summary?.total_treatments || 0}</div>
            </div>
            <div className="card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.totalRevenue', 'Total Revenue')}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{formatCurrency(data.summary?.total_revenue)}</div>
            </div>
            {/* Ramp stops are written out rather than var(--brand-*): this subtree is
                cloned into a bare print window with no theme.css, where the custom
                properties resolve to nothing and leave white text on a blank card. */}
            <div className="card" style={{ background: 'linear-gradient(135deg, #2F6B58 0%, #20463B 100%)', color: 'white' }}>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.avgPerTreatment', 'Avg per Treatment')}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{formatCurrency(data.summary?.avg_per_treatment)}</div>
            </div>
          </div>

          {/* By Category */}
          {data.by_category && data.by_category.length > 0 && (
            <div className="card" style={{ marginBottom: '1rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>{print_lang('reports.byCategory', 'By Category')}</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>{print_lang('reports.category', 'Category')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.count', 'Count')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.revenue', 'Revenue')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.percentage', '%')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_category.map((row, index) => {
                      const percentage = data.summary?.total_revenue > 0
                        ? ((row.revenue / data.summary.total_revenue) * 100).toFixed(1)
                        : 0;
                      return (
                        <tr key={index}>
                          <td>{row.category}</td>
                          <td style={{ textAlign: 'right' }}>{row.count}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(row.revenue)}</td>
                          <td style={{ textAlign: 'right' }}>{percentage}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* By Treatment */}
          {data.by_treatment && data.by_treatment.length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: '1rem' }}>{print_lang('reports.topTreatments', 'Top Treatments')}</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>{print_lang('reports.treatment', 'Treatment')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.count', 'Count')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.revenue', 'Revenue')}</th>
                      <th style={{ textAlign: 'right' }}>{print_lang('reports.avgPrice', 'Avg Price')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_treatment.map((row, index) => (
                      <tr key={index}>
                        <td>{currentLanguage === 'en' && row.name_en ? row.name_en : row.name}</td>
                        <td style={{ textAlign: 'right' }}>{row.count}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(row.revenue)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(row.avg_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentSummary;
